import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { isElectronRuntime } from '../../realm/isRealmEnabled';

const FALLBACK_DOWNLOAD =
  'https://github.com/techellipticadigim/workloop-distribution/releases';

const siblingPlatform = (platform) => {
  if (platform === 'DESKTOP_MAC') return 'DESKTOP_WIN';
  if (platform === 'DESKTOP_WIN') return 'DESKTOP_MAC';
  return null;
};

const fetchCheck = async (platform, versionCode) => {
  const { data } = await axios.get(`${API_URL}/app-updates/check`, {
    params: { platform, versionCode },
    timeout: 8000,
  });
  return data || null;
};

/**
 * On Electron launch: GET /api/app-updates/check?platform=DESKTOP_MAC|DESKTOP_WIN&versionCode=…
 * If updateType=MANDATORY, block the app until they install (or quit).
 * Children always mount so boot/splash still runs.
 */
const DesktopMandatoryUpdateGate = ({ children }) => {
  const [status, setStatus] = useState(() => (
    isElectronRuntime() ? 'checking' : 'ok'
  ));
  const [update, setUpdate] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isElectronRuntime()) return undefined;
    let cancelled = false;

    const applyMandatory = (data, versionInfo, platform) => {
      if (cancelled || data?.updateType !== 'MANDATORY') return false;
      setUpdate({
        latestVersionName: data.latestVersionName || 'a newer version',
        releaseNotes: data.releaseNotes || '',
        downloadUrl: data.downloadUrl || FALLBACK_DOWNLOAD,
        clientVersionCode: data.clientVersionCode || versionInfo?.versionCode,
        versionName: versionInfo?.versionName || '',
        platform,
      });
      setStatus('mandatory');
      return true;
    };

    const run = async ({ showChecking } = {}) => {
      if (cancelled) return;
      if (showChecking) setStatus((prev) => (prev === 'mandatory' ? prev : 'checking'));
      try {
        const versionInfo = await Promise.race([
          window.workloopRealm?.getAppVersion?.() ?? Promise.resolve(null),
          new Promise((resolve) => window.setTimeout(() => resolve(null), 2500)),
        ]);
        const versionCode = Number(versionInfo?.versionCode) || 0;
        const platform = versionInfo?.platform || 'DESKTOP_MAC';
        if (!versionCode) {
          if (!cancelled) setStatus((prev) => (prev === 'mandatory' ? prev : 'ok'));
          return;
        }

        let data = await fetchCheck(platform, versionCode);
        if (data?.updateType !== 'MANDATORY' && data?.configFound === false) {
          const other = siblingPlatform(platform);
          if (other) {
            const fallback = await fetchCheck(other, versionCode);
            if (fallback?.configFound) {
              data = fallback;
            }
          }
        }

        if (cancelled) return;
        if (applyMandatory(data, versionInfo, data?.platform || platform)) return;
        setStatus((prev) => (prev === 'mandatory' ? prev : 'ok'));
      } catch (err) {
        console.warn('[desktop-update] check failed', err?.message || err);
        if (!cancelled) {
          setError('');
          setStatus((prev) => (prev === 'mandatory' ? prev : 'ok'));
        }
      }
    };

    run({ showChecking: true });
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      run({ showChecking: false });
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const interval = window.setInterval(() => run({ showChecking: false }), 60 * 1000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(interval);
    };
  }, []);

  const openInstaller = async () => {
    const url = update?.downloadUrl || FALLBACK_DOWNLOAD;
    try {
      await window.workloopRealm?.openExternal?.(url);
    } catch (err) {
      setError(err?.message || 'Could not open download link');
      return;
    }
    window.setTimeout(() => {
      window.workloopRealm?.quitApp?.();
    }, 400);
  };

  const quitApp = () => {
    window.workloopRealm?.quitApp?.();
  };

  return (
    <>
      {children}
      {status === 'checking' ? (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-[#09090b] text-white">
          <div className="text-center px-6">
            <p className="text-[28px] font-bold tracking-tight">
              <span className="text-[#1cb695]">Work</span>
              <span className="text-[#2f7dff]">Loop</span>
            </p>
            <p className="mt-3 text-sm text-[#aeb6c8]">Checking for updates…</p>
          </div>
        </div>
      ) : null}
      {status === 'mandatory' && update ? (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-300">
                Mandatory update
              </p>
              <h2 className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
                Install Workloop {update.latestVersionName}
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                This version ({update.versionName || `build ${update.clientVersionCode}`}) is no longer supported.
                Please install the latest desktop app to continue.
              </p>
              {update.releaseNotes ? (
                <div className="mt-4 max-h-40 overflow-y-auto rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {update.releaseNotes}
                </div>
              ) : null}
              {error ? (
                <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{error}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 px-6 pb-6">
              <button
                type="button"
                onClick={openInstaller}
                className="w-full rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 text-sm"
              >
                Download & install
              </button>
              <button
                type="button"
                onClick={quitApp}
                className="w-full rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Quit Workloop
              </button>
              <p className="text-[11px] text-center text-gray-400 dark:text-gray-500">
                The app will close after you open the installer.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default DesktopMandatoryUpdateGate;
